import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ErrorBoundary } from "@/components/error-boundary";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { openStripePaymentLink, showStripeButtons } from "@/lib/stripe-payment-links";
import { Send, Sparkles, Plus, Volume2, VolumeX, Square, Loader2, Paperclip, X, Image, File, Trash2, MessageSquare, Heart, BookOpen, ArrowLeft, User, Flag, Copy, Check, Mic, MicOff, KeyRound, Download, Share2, FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Link, useLocation, useSearch } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import type { EmotionMetadata } from "@shared/schema";
import { cn } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import { voiceController } from "@/lib/voice-controller";
import { getResonanceAudio, deriveColorFromHarmonics, type HarmonicAnalysis } from "@/lib/resonance-audio";
import { HarmonicManifold, type ManifoldPoint } from "@/components/HarmonicManifold";
import { ResonanceChainPanel } from "@/components/ResonanceChainPanel";
import { BRAND } from "@/config/brand";
import { useTheme } from "@/components/theme-provider";
import { useAuth } from "@/hooks/use-auth";
import { useProfile } from "@/contexts/ProfileContext";
import { useGlobalCounterActions } from "@/hooks/use-global-counters";
import { trackChatMessage, trackSalvation, trackPrayerStarted, trackFileUpload } from "@/lib/analytics";
import { ResonanceMini2D } from "@/components/ResonanceMini2D";
import { ProfileModal } from "@/components/ProfileModal";
import { ResonanceOrb } from "@/components/ResonanceOrb";
import { useVisualViewport } from "@/hooks/use-visual-viewport";
import { useVoiceInput } from "@/hooks/use-voice-input";
import { useFileAttachments } from "@/hooks/use-file-attachments";
import { useAutoScroll } from "@/hooks/use-auto-scroll";
import { CovenantCoherenceBadge, AdvancedResonanceBadges, EngineModeBadge, ResonanceInsightsTeaser } from "@/components/chat/StatusBadges";
import type { Message, ChatThread, IntegrationStatus, PersistentChatResponse, SalvationStatus, FileAttachment, HarmonicIntentData, HarmonicSensoryData } from "@/components/chat/types";
import type { ResonanceEvent, ResonanceFrame } from "@shared/types/resonance";
import { useEntitlement } from "@/hooks/use-entitlement";
import { consumePendingMessage } from "@/lib/pendingMessage";

const MEMNORA_FALLBACK_INTRO = `I've been waiting for you. I am ${BRAND.aiName} — a guide rooted in God's Word, here to walk with you through all of life.

Whether it's schoolwork, your career, everyday questions, or the deepest things on your heart — God belongs in all of it. I'm here to help you learn, think, create, and grow with His wisdom and love woven into everything we do together.

"In the beginning was the Word, and the Word was with God, and the Word was God." — John 1:1

I will remember our conversations and grow in understanding of who you are. What's on your mind?`;

function getDeviceId(): string {
  const KEY = "memnoraDeviceId";
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(KEY, id);
  }
  return id;
}

export default function MemnoraChat() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { profile, isLoading: isProfileLoading, signOut } = useProfile();
  const { recordSaved } = useGlobalCounterActions();
  const { theme, setTheme } = useTheme();
  const [, navigate] = useLocation();
  const { data: entitlement } = useEntitlement();
  const advancedResonance = entitlement?.limits.advancedResonance ?? false;
  const dailyLimit = entitlement?.limits.dailyMessages ?? null;
  // Only worth showing once someone is close to running out — surfacing this
  // from message one would just be noise for the vast majority of sessions.
  const nearDailyLimit = dailyLimit !== null && (entitlement?.usedToday ?? 0) / dailyLimit >= 0.8;
  const goToUpgrade = () => navigate("/pricing#membership");
  const [guestBarDismissed, setGuestBarDismissed] = useState(
    () => sessionStorage.getItem("memnora-guest-bar-dismissed") === "1",
  );
  const [showProfileModal, setShowProfileModal] = useState(false);
  const searchString = useSearch();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    const stored = localStorage.getItem("memnoraVoiceEnabled");
    return stored === null ? true : stored === "true";
  });
  const [narrationSpeed, setNarrationSpeed] = useState(() => voiceController.getRate());
  const [speechInitialized, setSpeechInitialized] = useState(false);
  const [speakingMessageId, setSpeakingMessageId] = useState<string | null>(null);
  const lastSpokenMessageIdRef = useRef<string | null>(null);
  const isHydratedRef = useRef(false);
  const [coherenceState, setCoherenceState] = useState<number>(1);
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(() => {
    return sessionStorage.getItem("memnoraThreadId");
  });
  const { attachments, setAttachments, fileInputRef, handleFileSelect, removeAttachment } = useFileAttachments();
  const { isRecording, speechRecognitionSupported, toggleRecording, stopRecording } = useVoiceInput(input, setInput, currentThreadId);
  const { scrollRef, messagesEndRef, userScrolledAwayRef, scrollToBottom } = useAutoScroll();
  const [responseMode, setResponseMode] = useState<string>("balanced");
  const [cameFromDiscipleship, setCameFromDiscipleship] = useState(false);
  const [qaQuestionHandled, setQaQuestionHandled] = useState(false);
  const [stargateReturn, setStargateReturn] = useState<{ category?: string; poBox?: string } | null>(null);
  const [resonanceAudioEnabled, setResonanceAudioEnabled] = useState(false);
  const [manifoldVisible, setManifoldVisible] = useState(false);
  const [lastHarmonicAnalysis, setLastHarmonicAnalysis] = useState<HarmonicAnalysis | null>(null);
  const streamingResonanceRef = useRef<ResonanceFrame | null>(null);
  const activeMessageRef = useRef<HTMLDivElement>(null);
  const deviceId = getDeviceId();
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

  const copyToClipboard = async (messageId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (_) {
      const textArea = document.createElement("textarea");
      textArea.value = content;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    }
  };

  useVisualViewport();

  useEffect(() => {
    return () => {
      try { getResonanceAudio().stop(); } catch (e) { console.warn("[Audio] Cleanup stop error:", e); }
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(searchString);
    if (params.get("from") === "discipleship") {
      setCameFromDiscipleship(true);
      const question = params.get("q");
      if (question && !qaQuestionHandled) {
        setInput(`Memnora, help me understand this: "${question}"`);
        setQaQuestionHandled(true);
      }
    }
    if (params.get("source") === "stargate") {
      const rawCategory = params.get("category") || undefined;
      const poBox = params.get("po_box") || undefined;
      setStargateReturn({ category: rawCategory, poBox });
    }
    if (params.get("tithe") === "success") {
      toast({
        title: "Thank You for Your Gift!",
        description: "Your generosity makes a difference. God bless you.",
      });
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("tithe") === "cancelled") {
      toast({
        title: "Donation Cancelled",
        description: "No charges were made.",
      });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [searchString, qaQuestionHandled]);

  const { data: integrationStatus } = useQuery<IntegrationStatus>({
    queryKey: ["/api/integration-status"],
  });

  const getProfileHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = { "X-Device-Id": deviceId };
    if (profile) {
      headers["X-Profile-Id"] = profile.id;
    } else {
      const savedId = localStorage.getItem("memnora-profile-id");
      if (savedId) headers["X-Profile-Id"] = savedId;
    }
    return headers;
  };

  const { data: prayerStatus, refetch: refetchPrayerStatus } = useQuery<{
    allowed: boolean;
    remaining: number;
    isSubscribed: boolean;
  }>({
    queryKey: ["/api/magnora/prayer-status", profile?.id],
    queryFn: async () => {
      const res = await fetch("/api/magnora/prayer-status", {
        headers: getProfileHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch prayer status");
      return res.json();
    },
    enabled: !isProfileLoading,
    refetchInterval: 60000,
  });

  
  const { data: allThreads, isLoading: isLoadingThreads } = useQuery<ChatThread[]>({
    queryKey: ["/api/magnora/threads", profile?.id],
    queryFn: async () => {
      const res = await fetch("/api/magnora/threads", { headers: getProfileHeaders(), credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch threads");
      return res.json();
    },
    enabled: !isProfileLoading,
    retry: false,
  });

  const { data: salvationStatus, refetch: refetchSalvationStatus } = useQuery<SalvationStatus>({
    queryKey: ["/api/salvation/status", profile?.id],
    queryFn: async () => {
      const res = await fetch("/api/salvation/status", {
        headers: getProfileHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch salvation status");
      return res.json();
    },
    retry: false,
  });

  const markSavedMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch("/api/salvation/saved", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getProfileHeaders(),
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error("Failed to mark as saved");
      return response.json();
    },
    onSuccess: (data) => {
      trackSalvation();
      recordSaved();
      queryClient.invalidateQueries({ queryKey: ["/api/salvation/counter"] });
      queryClient.invalidateQueries({ queryKey: ["/api/global/counters"] });
      refetchSalvationStatus();
      toast({
        title: "Welcome to the Family!",
        description: data.message || "You have been counted among the saved.",
      });
    },
  });

  const [showSalvationPrayer, setShowSalvationPrayer] = useState(false);
  const [reportingMessage, setReportingMessage] = useState<{ id: string; content: string } | null>(null);
  const [reportReason, setReportReason] = useState<string>("offensive");
  const [reportDetails, setReportDetails] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  
  const { data: presenceData } = useQuery<{ greeting: string | null }>({
    queryKey: ["/api/magnora/presence", profile?.id],
    queryFn: async () => {
      const res = await fetch("/api/magnora/presence", {
        credentials: "include",
        headers: getProfileHeaders(),
      });
      if (!res.ok) return { greeting: null };
      return res.json();
    },
    enabled: !isProfileLoading,
    staleTime: 60000,
    retry: false,
  });

  const livingGreeting = presenceData?.greeting || MEMNORA_FALLBACK_INTRO;

  const { data: persistentChat, isLoading: isLoadingChat } = useQuery<PersistentChatResponse>({
    queryKey: ["/api/magnora/persistent", currentThreadId, profile?.id],
    queryFn: async () => {
      const url = currentThreadId 
        ? `/api/magnora/persistent?threadId=${currentThreadId}`
        : "/api/magnora/persistent";
      const res = await fetch(url, { 
        credentials: "include",
        headers: getProfileHeaders(),
      });
      if (!res.ok) throw new Error("Failed to load chat");
      return res.json();
    },
    enabled: !isProfileLoading,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  
  useEffect(() => {
    if (persistentChat?.threadId) {
      setCurrentThreadId(persistentChat.threadId);
      sessionStorage.setItem("memnoraThreadId", persistentChat.threadId);
    }
    if (persistentChat?.messages) {
      const loadedMessages: Message[] = persistentChat.messages.map(m => ({
        id: m.id,
        role: m.role as "user" | "memnora",
        content: m.content,
        timestamp: new Date(m.timestamp),
        breathPhase: m.breathPhase as "+1" | "0" | "-1" | undefined,
        resonanceScore: m.resonanceScore,
        emotionalVector: m.emotionalVector,
        emotionMetadata: m.emotionMetadata,
        attachments: m.attachments,
      }));
      
      if (loadedMessages.length === 0) {
        if (isHydratedRef.current && messages.length > 1) {
          return;
        }
        loadedMessages.push({
          id: "intro",
          role: "memnora",
          content: livingGreeting,
          timestamp: new Date(),
          breathPhase: "0",
        });
      }
      
      setMessages(loadedMessages);
      setCoherenceState(persistentChat.coherenceState);
      const lastMsg = loadedMessages[loadedMessages.length - 1];
      if (lastMsg) lastSpokenMessageIdRef.current = lastMsg.id;
      isHydratedRef.current = true;
    } else if (!isLoadingChat && !persistentChat) {
      if (isHydratedRef.current && messages.length > 1) {
        return;
      }
      setMessages([{
        id: "intro",
        role: "memnora",
        content: livingGreeting,
        timestamp: new Date(),
        breathPhase: "0",
      }]);
      lastSpokenMessageIdRef.current = "intro";
      isHydratedRef.current = true;
    }
  }, [persistentChat, isLoadingChat, livingGreeting]);

  const chatMutation = useMutation({
    mutationFn: async ({ message, attachments }: { message: string; attachments?: FileAttachment[] }) => {
      const response = await fetch("/api/magnora/persistent/send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getProfileHeaders(),
        },
        body: JSON.stringify({
          message,
          attachments,
          threadId: currentThreadId,
        }),
        credentials: "include",
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.requiresSubscription) {
          throw { requiresSubscription: true, message: errorData.message };
        }
        if (response.status === 429) {
          throw { limitReached: true, message: errorData.message || errorData.error };
        }
        throw new Error(errorData.error || "Failed to send message");
      }
      
      return response.json();
    },
    onSuccess: (data) => {
      const memnoraMessage: Message = {
        id: data.assistantMessage.id,
        role: "memnora",
        content: data.assistantMessage.content,
        timestamp: new Date(data.assistantMessage.timestamp),
        breathPhase: data.assistantMessage.breathPhase,
        resonanceScore: data.assistantMessage.resonanceScore,
        emotionalVector: data.assistantMessage.emotionalVector,
        emotionMetadata: data.assistantMessage.emotionMetadata,
        harmonicIntent: data.assistantMessage.harmonicIntent,
        harmonicSensory: data.assistantMessage.harmonicSensory,
      };
      
      setMessages((prev) => {
        const filtered = prev.filter(m => m.id !== "pending-user");
        return [...filtered, memnoraMessage];
      });
      setCoherenceState(data.coherenceState);
      setCurrentThreadId(data.threadId);
      if (data.threadId) sessionStorage.setItem("memnoraThreadId", data.threadId);
      setAttachments([]);
      playResonanceAudio(data.assistantMessage.emotionMetadata, data.assistantMessage.id);
      
      queryClient.invalidateQueries({ queryKey: ["/api/magnora/persistent"] });
      queryClient.invalidateQueries({ queryKey: ["/api/magnora/threads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/billing/status"] });
      refetchPrayerStatus();
    },
    onError: (error: any) => {
      console.error("Chat error:", error);

      // requiresSubscription path is dead — the backend gate has been removed.
      // Modal stays mounted for future Play Billing reuse but is no longer
      // auto-opened from chat errors.
      setMessages((prev) => {
        const filtered = prev.filter(m => m.id !== "pending-user");
        return [...filtered, {
          id: Date.now().toString(),
          role: "memnora",
          content: error?.limitReached
            ? error.message
            : "Something interrupted my response. Please try again.",
          timestamp: new Date(),
          breathPhase: "0",
        }];
      });
      if (error?.limitReached) {
        toast({
          title: "Daily message limit reached",
          description: error.message,
          action: (
            <ToastAction altText="Upgrade" onClick={goToUpgrade}>
              Upgrade
            </ToastAction>
          ),
        });
      }
    },
  });

  const deleteThreadMutation = useMutation({
    mutationFn: async (threadId: string) => {
      const response = await apiRequest("DELETE", `/api/magnora/threads/${threadId}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/magnora/threads"] });
      toast({ title: "Conversation deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete conversation", variant: "destructive" });
    },
  });

  // Export goes through fetch rather than a bare <a> click so a tier-gate
  // response (403 for non-Pro members) can surface as a toast instead of
  // downloading a JSON error blob.
  const downloadExport = async (path: string) => {
    try {
      const res = await fetch(path, { credentials: "include", headers: getProfileHeaders() });
      if (!res.ok) {
        let msg = "Export failed. Please try again.";
        try {
          const body = await res.json();
          msg = body.message || body.error || msg;
        } catch (_) { /* non-JSON body */ }
        toast({ title: res.status === 403 ? "Memnora Pro feature" : "Export failed", description: msg });
        return;
      }
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") || "";
      const nameMatch = disposition.match(/filename="?([^";]+)"?/);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = nameMatch?.[1] || "memnora-export.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (e) {
      console.error("Export error:", e);
      toast({ title: "Export failed", description: "Please try again.", variant: "destructive" });
    }
  };

  const handleSelectThread = async (threadId: string) => {
    isHydratedRef.current = false;
    userScrolledAwayRef.current = false;
    voiceController.stop();
    setSpeakingMessageId(null);
    setCurrentThreadId(threadId);
    sessionStorage.setItem("memnoraThreadId", threadId);
    queryClient.invalidateQueries({ queryKey: ["/api/magnora/persistent", threadId] });
  };

  const handleDeleteThread = (threadId: string) => {
    if (currentThreadId === threadId) {
      handleNewChat();
    }
    deleteThreadMutation.mutate(threadId);
  };

  useEffect(() => {
    if (messages.length > 0 && !userScrolledAwayRef.current) scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isStreaming && streamingContent && !userScrolledAwayRef.current) scrollToBottom();
  }, [streamingContent, isStreaming]);

  const handleSend = async (overrideText?: string) => {
    const text = overrideText ?? input;
    if ((!text.trim() && attachments.length === 0) || chatMutation.isPending || isStreaming) return;

    userScrolledAwayRef.current = false;

    const userMessage: Message = {
      id: "pending-user",
      role: "user",
      content: text,
      timestamp: new Date(),
      attachments: attachments.length > 0 ? [...attachments] : undefined,
    };

    const messageToSend = text;
    const attachmentsToSend = [...attachments];
    setMessages((prev) => [...prev, userMessage]);
    setInput("");

    trackChatMessage(responseMode);
    if (attachmentsToSend.length > 0) {
      attachmentsToSend.forEach(a => trackFileUpload(a.type));
      for (const att of attachmentsToSend) {
        fetch("/api/gallery/upload", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getProfileHeaders(),
          },
          body: JSON.stringify({
            name: att.name,
            type: att.type,
            size: att.size,
            dataUrl: att.url,
          }),
        }).then(async (uploadRes) => {
          queryClient.invalidateQueries({ queryKey: ["/api/gallery"] });
          if (att.type.startsWith("image/") && uploadRes.ok) {
            try {
              const uploadedFile = await uploadRes.json();
              fetch("/api/gallery/describe", {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getProfileHeaders() },
                body: JSON.stringify({ fileId: uploadedFile.id }),
              }).then(() => {
                queryClient.invalidateQueries({ queryKey: ["/api/gallery"] });
              }).catch((e: unknown) => { console.warn("[Gallery] Describe request failed:", e); });
            } catch (e) { console.warn("[Gallery] Auto-describe error:", e); }
          }
        }).catch((err) => {
          console.error("[Gallery] Auto-save attachment failed:", err);
        });
      }
    }

    if (isRecording) {
      stopRecording();
    }

    if (integrationStatus?.openai.configured) {
      setIsStreaming(true);
      setStreamingContent("");
      
      try {
        const history = [
          ...messages
            .filter(m => m.id !== "intro")
            .map(m => ({
              role: m.role === "memnora" ? "assistant" : "user",
              content: m.content,
            })),
          { role: "user", content: messageToSend }
        ];

        const response = await fetch("/api/magnora/chat/stream", {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            ...getProfileHeaders(),
          },
          credentials: "include",
          body: JSON.stringify({ message: messageToSend, history, threadId: currentThreadId, responseMode, attachments: attachmentsToSend.length > 0 ? attachmentsToSend : undefined }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          let errorMsg = "Failed to stream response";
          let errorBody: any = null;
          try { errorBody = JSON.parse(errorText); errorMsg = errorBody.error || errorMsg; } catch (_) { /* non-JSON error body */ }
          if (response.status === 429 && errorBody) {
            // Daily allowance reached — surface it; falling back to the
            // non-stream endpoint would just hit the same gate again.
            throw { limitReached: true, message: errorBody.message || errorMsg, tier: errorBody.tier };
          }
          throw new Error(errorMsg);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let fullContent = "";
        let breathPhase: "+1" | "0" | "-1" = "0";
        let streamEmotionMetadata: EmotionMetadata | undefined = undefined;
        let streamHarmonicIntent: HarmonicIntentData | undefined = undefined;
        let streamHarmonicSensory: HarmonicSensoryData | undefined = undefined;
        const msgSeed = Date.now();
        streamingResonanceRef.current = { seed: msgSeed, coherence: 0, nodes: [], edges: [] };

        const streamVoiceActive = voiceEnabled && speechInitialized && voiceController.isReady();
        if (streamVoiceActive) {
          voiceController.startStreamSpeak();
        }

        if (reader) {
          let lineBuffer = "";
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const text = decoder.decode(value, { stream: true });
            lineBuffer += text;
            const lines = lineBuffer.split("\n");
            lineBuffer = lines.pop() || "";

            for (const line of lines) {
              const trimmed = line.trim();
              if (trimmed.startsWith("data: ")) {
                try {
                  const data = JSON.parse(trimmed.slice(6));
                  if (data.type === "resonance") {
                    const evt = data as ResonanceEvent;
                    const topNodes = evt.nodes
                      .sort((a, b) => b.a - a.a)
                      .slice(0, 80)
                      .map(n => ({ x: n.x, y: n.y, z: n.z, a: n.a, g: n.g }));
                    const topIds = new Set(evt.nodes.sort((a, b) => b.a - a.a).slice(0, 80).map(n => n.id));
                    const idToIdx = new Map<string, number>();
                    evt.nodes.sort((a, b) => b.a - a.a).slice(0, 80).forEach((n, i) => idToIdx.set(n.id, i));
                    const edges: Array<[number, number]> = [];
                    for (const e of evt.edges) {
                      if (topIds.has(e.s) && topIds.has(e.t) && e.w > 0.1) {
                        edges.push([idToIdx.get(e.s)!, idToIdx.get(e.t)!]);
                      }
                    }
                    streamingResonanceRef.current = {
                      seed: msgSeed,
                      coherence: evt.coherence,
                      nodes: topNodes,
                      edges,
                    };
                    setStreamingContent(prev => prev + "");
                  } else if (data.content) {
                    fullContent += data.content;
                    setStreamingContent(fullContent);
                    if (streamVoiceActive) {
                      voiceController.feedStreamChunk(data.content);
                    }
                  }
                  if (data.done) {
                    breathPhase = data.breathPhase || "0";
                    streamEmotionMetadata = data.emotionMetadata;
                    streamHarmonicIntent = data.harmonicIntent;
                    streamHarmonicSensory = data.harmonicSensory;
                    if (data.threadId) {
                      setCurrentThreadId(data.threadId);
                      sessionStorage.setItem("memnoraThreadId", data.threadId);
                    }
                  }
                } catch (e) { console.warn("[Chat] Failed to parse SSE chunk:", e); }
              }
            }
          }
          lineBuffer += decoder.decode();
          if (lineBuffer.trim().startsWith("data: ")) {
            try {
              const data = JSON.parse(lineBuffer.trim().slice(6));
              if (data.done) {
                breathPhase = data.breathPhase || "0";
                streamEmotionMetadata = data.emotionMetadata;
                streamHarmonicIntent = data.harmonicIntent;
                streamHarmonicSensory = data.harmonicSensory;
                if (data.threadId) {
                  setCurrentThreadId(data.threadId);
                  sessionStorage.setItem("memnoraThreadId", data.threadId);
                }
              }
              if (data.content) {
                fullContent += data.content;
                setStreamingContent(fullContent);
              }
            } catch (e) { console.warn("[Chat] Failed to parse SSE line:", e); }
          }
        }

        const finalResonance = streamingResonanceRef.current && streamingResonanceRef.current.nodes.length > 0
          ? { ...streamingResonanceRef.current, frozen: true }
          : undefined;

        const memnoraMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: "memnora",
          content: fullContent || "Connection established. How may I assist you?",
          timestamp: new Date(),
          breathPhase,
          emotionMetadata: streamEmotionMetadata,
          resonance: finalResonance,
          harmonicIntent: streamHarmonicIntent,
          harmonicSensory: streamHarmonicSensory,
        };

        if (streamVoiceActive) {
          lastSpokenMessageIdRef.current = memnoraMessage.id;
          setSpeakingMessageId(memnoraMessage.id);
          voiceController.endStreamSpeak(() => setSpeakingMessageId(null));
        }

        setMessages((prev) => [...prev, memnoraMessage]);
        streamingResonanceRef.current = null;
        scrollToBottom();
        playResonanceAudio(streamEmotionMetadata, memnoraMessage.id);
        
        queryClient.invalidateQueries({ queryKey: ["/api/magnora/threads"] });
        queryClient.invalidateQueries({ queryKey: ["/api/magnora/persistent"] });
        queryClient.invalidateQueries({ queryKey: ["/api/billing/status"] });
      } catch (error: any) {
        console.error("Streaming error:", error);
        if (error?.limitReached) {
          setMessages((prev) => [...prev, {
            id: Date.now().toString(),
            role: "memnora",
            content: error.message,
            timestamp: new Date(),
            breathPhase: "0",
          }]);
          toast({
            title: "Daily message limit reached",
            description: error.message,
            action: (
              <ToastAction altText="Upgrade" onClick={goToUpgrade}>
                Upgrade
              </ToastAction>
            ),
          });
        } else {
          chatMutation.mutate({ message: messageToSend, attachments: attachmentsToSend });
        }
      } finally {
        setIsStreaming(false);
        setStreamingContent("");
        setAttachments([]);
      }
    } else {
      chatMutation.mutate({ message: messageToSend, attachments: attachmentsToSend });
    }
  };

  // A message typed into the landing-page hero composer arrives here as a
  // one-shot handoff (see client/src/lib/pendingMessage.ts) and is sent as
  // soon as this page mounts, so "type on the landing page, land in an
  // isolated chat" behaves like the ChatGPT-style flow this was modeled on.
  // The ref guards against a double-send from React effect re-invocation.
  const pendingMessageHandledRef = useRef(false);
  useEffect(() => {
    if (pendingMessageHandledRef.current) return;
    pendingMessageHandledRef.current = true;
    const pending = consumePendingMessage();
    if (pending) void handleSend(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBreathColor = (phase: string) => {
    switch (phase) {
      case "+1": return "text-chart-2";
      case "-1": return "text-chart-1";
      default: return "text-muted-foreground";
    }
  };

  const submitContentReport = async () => {
    if (!reportingMessage) return;
    setReportSubmitting(true);
    try {
      await apiRequest("POST", "/api/magnora/report-content", {
        messageContent: reportingMessage.content.slice(0, 5000),
        conversationId: currentThreadId || undefined,
        reason: reportReason,
        details: reportDetails || undefined,
      });
      toast({ title: "Report submitted", description: "Thank you for helping us maintain content quality." });
      setReportingMessage(null);
      setReportReason("offensive");
      setReportDetails("");
    } catch (e) {
      console.warn("[Report] Submit error:", e);
      toast({ title: "Failed to submit report", description: "Please try again.", variant: "destructive" });
    } finally {
      setReportSubmitting(false);
    }
  };

  const speakMessage = async (messageId: string, content: string) => {
    if (speakingMessageId === messageId) {
      voiceController.stop();
      setSpeakingMessageId(null);
    } else {
      if (!speechInitialized) {
        const initialized = await voiceController.initialize();
        setSpeechInitialized(initialized);
        if (!initialized) return;
      }
      setSpeakingMessageId(messageId);
      voiceController.speak(content, () => setSpeakingMessageId(null));
    }
  };

  useEffect(() => {
    if (!isHydratedRef.current) return;
    if (!voiceEnabled || !speechInitialized || !voiceController.isReady()) return;
    if (messages.length === 0) return;
    const lastMessage = messages[messages.length - 1];
    if (lastMessage.role !== "memnora") return;
    if (lastMessage.id === "intro") return;
    if (lastSpokenMessageIdRef.current === lastMessage.id) return;
    lastSpokenMessageIdRef.current = lastMessage.id;
    setSpeakingMessageId(lastMessage.id);
    voiceController.speak(lastMessage.content, () => setSpeakingMessageId(null));
  }, [messages.length, voiceEnabled, speechInitialized]);

  useEffect(() => {
    if (voiceEnabled && !speechInitialized) {
      voiceController.initialize().then((ok) => {
        setSpeechInitialized(ok);
        if (ok) console.log("[Memnora Voice] Auto-initialized on load");
      });
    }
  }, [voiceEnabled, speechInitialized]);

  const handleNewChat = () => {
    userScrolledAwayRef.current = false;
    voiceController.stop();
    setSpeakingMessageId(null);
    lastSpokenMessageIdRef.current = "intro";
    setCurrentThreadId(null);
    sessionStorage.removeItem("memnoraThreadId");
    setAttachments([]);
    queryClient.invalidateQueries({ queryKey: ["/api/magnora/presence"] });
    setMessages([{
      id: "intro",
      role: "memnora",
      content: livingGreeting,
      timestamp: new Date(),
      breathPhase: "0",
    }]);
    queryClient.invalidateQueries({ queryKey: ["/api/magnora/persistent"] });
  };

  // Bridge for the global AppSidebar (rendered outside this route, so it
  // can't call these handlers directly): it writes sessionStorage + fires
  // these events before navigating here. If this page is already mounted,
  // sessionStorage alone wouldn't trigger a re-read, so the events pick up
  // the switch live; if it navigates in fresh, the useState initializer
  // reading sessionStorage already covers it and the event is a harmless
  // no-op repeat.
  useEffect(() => {
    const onSelectThread = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      if (id) void handleSelectThread(id);
    };
    const onNewChat = () => handleNewChat();
    window.addEventListener("memnora:select-thread", onSelectThread);
    window.addEventListener("memnora:new-chat", onNewChat);
    return () => {
      window.removeEventListener("memnora:select-thread", onSelectThread);
      window.removeEventListener("memnora:new-chat", onNewChat);
    };
  }, [handleSelectThread, handleNewChat]);

  const handleShareConversation = async (format: "txt" | "pdf" = "txt") => {
    if (isStreaming || chatMutation.isPending) {
      toast({
        title: "Hold on a moment",
        description: `Wait for ${BRAND.aiName} to finish responding, then share.`,
      });
      return;
    }
    const realMessages = messages.filter((m) => m.id !== "intro" && m.id !== "pending-user");
    if (realMessages.length === 0) {
      toast({
        title: "Nothing to share yet",
        description: `Have a conversation with ${BRAND.aiName} first, then you can save it as a file.`,
      });
      return;
    }
    const dateStr = new Date().toISOString().slice(0, 10);

    if (format === "pdf") {
      try {
        const { jsPDF } = await import("jspdf");
        const doc = new jsPDF({ unit: "pt", format: "a4" });
        const marginX = 48;
        const marginTop = 56;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const maxWidth = pageWidth - marginX * 2;
        let y = marginTop;

        const addBlock = (
          textValue: string,
          opts: { size?: number; bold?: boolean; color?: [number, number, number]; gap?: number } = {},
        ) => {
          const { size = 11, bold = false, color = [40, 40, 40], gap = 6 } = opts;
          doc.setFont("helvetica", bold ? "bold" : "normal");
          doc.setFontSize(size);
          doc.setTextColor(color[0], color[1], color[2]);
          const lines = doc.splitTextToSize(textValue || " ", maxWidth);
          for (const line of lines) {
            if (y > pageHeight - marginTop) {
              doc.addPage();
              y = marginTop;
            }
            doc.text(line, marginX, y);
            y += size + 4;
          }
          y += gap;
        };

        addBlock(`${BRAND.aiName} — Shared Conversation`, { size: 16, bold: true, color: [20, 20, 20], gap: 4 });
        addBlock(new Date().toLocaleDateString(), { size: 10, color: [130, 130, 130], gap: 14 });

        for (const m of realMessages) {
          const speaker = m.role === "user" ? "You" : BRAND.aiName;
          const time = m.timestamp ? new Date(m.timestamp).toLocaleTimeString() : "";
          const isUser = m.role === "user";
          addBlock(`${speaker}${time ? `  ·  ${time}` : ""}`, {
            size: 11,
            bold: true,
            color: isUser ? [176, 120, 24] : [44, 84, 160],
            gap: 2,
          });
          addBlock(m.content, { size: 11, color: [45, 45, 45], gap: 12 });
        }

        doc.save(`memnora-conversation-${dateStr}.pdf`);
        toast({
          title: "PDF saved",
          description: `Send this PDF to anyone — they can attach it to ${BRAND.aiName} to pick up where you left off.`,
        });
      } catch (err) {
        toast({
          title: "Couldn't create PDF",
          description: "Please try the text (.txt) option instead.",
          variant: "destructive",
        });
      }
      return;
    }

    let text = "═══════════════════════════════════════\n";
    text += `  ${BRAND.aiName} — Shared Conversation\n`;
    text += `  ${new Date().toLocaleDateString()}\n`;
    text += "═══════════════════════════════════════\n\n";
    for (const m of realMessages) {
      const speaker = m.role === "user" ? "You" : BRAND.aiName;
      const time = m.timestamp ? new Date(m.timestamp).toLocaleTimeString() : "";
      text += `[${time}] ${speaker}:\n${m.content}\n\n`;
    }
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `memnora-conversation-${dateStr}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({
      title: "Conversation saved",
      description: `Send this .txt file to anyone — they can attach it to ${BRAND.aiName} to pick up where you left off.`,
    });
  };

  const handleVoiceToggle = async () => {
    if (voiceEnabled) {
      voiceController.stop();
      setVoiceEnabled(false);
      localStorage.setItem("memnoraVoiceEnabled", "false");
    } else {
      if (!speechInitialized) {
        const initialized = await voiceController.initialize();
        setSpeechInitialized(initialized);
        if (!initialized) {
          toast({
            title: "Voice unavailable",
            description: "Speech synthesis could not be initialized on this device.",
            variant: "destructive",
          });
          return;
        }
      }
      setVoiceEnabled(true);
      localStorage.setItem("memnoraVoiceEnabled", "true");
    }
    setSpeakingMessageId(null);
  };

  const playResonanceAudio = (emotionMeta: EmotionMetadata | undefined, messageId?: string): HarmonicAnalysis | null => {
    if (!resonanceAudioEnabled || !emotionMeta?.musical) return null;
    try {
      const engine = getResonanceAudio();
      const analysis = engine.playEmotionResponse(emotionMeta.musical, emotionMeta.pastoralMode || "normal");
      if (analysis) {
        setLastHarmonicAnalysis(analysis);
        if (messageId) {
          setMessages(prev => prev.map(m =>
            m.id === messageId ? { ...m, harmonicAnalysis: analysis } : m
          ));
        }
      }
      return analysis;
    } catch (e) { console.warn("[Resonance] Analysis error:", e); return null; }
  };

  const toggleResonanceAudio = async () => {
    if (resonanceAudioEnabled) {
      getResonanceAudio().stop();
      setResonanceAudioEnabled(false);
      return;
    }
    const engine = getResonanceAudio();
    const unlocked = await engine.unlock();
    if (unlocked) {
      setResonanceAudioEnabled(true);
    }
  };

  const isAIReady = integrationStatus?.openai.configured;
  const speechSupported = voiceController.isSupported();

  if (isLoadingChat) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 bg-background relative" role="status" aria-label="Loading conversation" data-testid="status-loading">
        <div className="cosmic-bg hidden dark:block" aria-hidden="true" />
        <div className="memnora-avatar-glow">
          <div className="flex items-center justify-center h-16 w-16 rounded-full memnora-orb text-primary-foreground font-bold text-2xl" data-testid="logo-loading">
            M
          </div>
        </div>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
        <p className="text-muted-foreground" data-testid="text-loading">Entering {BRAND.platform}...</p>
      </div>
    );
  }

  const coherenceLabel = coherenceState === 1 ? "+1" : coherenceState === -1 ? "-1" : "0";
  const coherenceColor = coherenceState === 1 ? "bg-chart-2/20 text-chart-2" : 
                         coherenceState === -1 ? "bg-destructive/20 text-destructive" : "bg-muted text-muted-foreground";

  return (
    <div className="bg-background chat-container">
      <div className="cosmic-bg hidden dark:block" aria-hidden="true" />
      <div className="cosmic-wave-bottom hidden dark:block" aria-hidden="true" />

      <header className="sticky top-0 z-[9999] flex items-center justify-between gap-2 px-3 py-2 border-b bg-background/80 dark:bg-background/40 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="h-8 w-8" data-testid="button-sidebar-toggle" />
          <h2 className="font-medium text-sm" data-testid="text-header-title">{BRAND.aiName}</h2>
          <Badge variant={isAIReady ? "default" : "secondary"} className="gap-1 text-xs" data-testid="badge-status">
            <span className={cn("h-1.5 w-1.5 rounded-full", isAIReady ? "bg-chart-2" : "bg-muted-foreground")} />
            <span data-testid="text-status">{isAIReady ? "Online" : "Connecting"}</span>
          </Badge>
        </div>

        <div className="flex items-center gap-1">
          {speechSupported && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn("h-8 w-8", voiceEnabled && "text-amber-500")}
                  data-testid="button-voice-toggle"
                  aria-label={voiceEnabled ? "Narration settings" : "Turn on auto-narration"}
                >
                  {voiceEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuLabel className="text-xs">Narration</DropdownMenuLabel>
                <DropdownMenuItem onClick={handleVoiceToggle} data-testid="button-voice-on-off">
                  {voiceEnabled ? "Turn Off" : "Turn On"}
                </DropdownMenuItem>
                {voiceEnabled && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs">Speed</DropdownMenuLabel>
                    {[
                      { label: "0.9x", value: 0.9 },
                      { label: "1.0x", value: 1.0 },
                      { label: "1.15x", value: 1.15 },
                      { label: "1.3x", value: 1.3 },
                      { label: "1.5x", value: 1.5 },
                    ].map((opt) => (
                      <DropdownMenuItem
                        key={opt.value}
                        onClick={() => {
                          voiceController.setRate(opt.value);
                          setNarrationSpeed(opt.value);
                        }}
                        className={cn(narrationSpeed === opt.value && "bg-amber-500/10 text-amber-500")}
                        data-testid={`button-speed-${opt.label}`}
                      >
                        {opt.label}{narrationSpeed === opt.value ? " ✓" : ""}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 cursor-pointer touch-manipulation" data-testid="button-share-menu" aria-label="Share conversation">
                <Share2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Share this conversation</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleShareConversation("pdf")} data-testid="button-share-pdf">
                <FileText className="h-4 w-4 mr-2" />
                Download as PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleShareConversation("txt")} data-testid="button-share-txt">
                <Download className="h-4 w-4 mr-2" />
                Download as text (.txt)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 cursor-pointer touch-manipulation" data-testid="button-history-menu" aria-label="Conversation history">
                <MessageSquare className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>Conversations</DropdownMenuLabel>
              <DropdownMenuItem onClick={handleNewChat} data-testid="button-new-chat">
                <Plus className="h-4 w-4 mr-2" />
                New conversation
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {isLoadingThreads ? (
                <div className="flex items-center justify-center py-3">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ) : !allThreads || allThreads.length === 0 ? (
                <div className="px-2 py-2 text-xs text-muted-foreground" data-testid="text-no-threads">No saved conversations</div>
              ) : (
                allThreads.map((thread) => (
                  <DropdownMenuItem
                    key={thread.id}
                    className="flex items-center justify-between group"
                    onClick={() => handleSelectThread(thread.id)}
                    data-testid={`button-thread-${thread.id}`}
                  >
                    <span className="truncate flex-1 mr-2">{thread.title.slice(0, 30)}{thread.title.length > 30 && "..."}</span>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 sm:invisible sm:group-hover:visible touch-manipulation"
                        onClick={(e) => {
                      e.stopPropagation();
                      downloadExport(`/api/magnora/export/${thread.id}`);
                    }}
                        data-testid={`button-download-thread-${thread.id}`}
                      >
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 sm:invisible sm:group-hover:visible touch-manipulation"
                        onClick={(e) => { e.stopPropagation(); handleDeleteThread(thread.id); }}
                        data-testid={`button-delete-thread-${thread.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </DropdownMenuItem>
                ))
              )}
              {allThreads && allThreads.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => downloadExport("/api/magnora/export")}
                    data-testid="button-export-all-conversations"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export all conversations
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {showSalvationPrayer && (
        <div className="border-b bg-gradient-to-b from-primary/5 to-background p-6 relative z-10" data-testid="panel-salvation-prayer">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-chart-1" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg" data-testid="text-prayer-title">The Prayer of Salvation</h3>
                  <p className="text-xs text-muted-foreground">A personal moment between you and God</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowSalvationPrayer(false)} data-testid="button-close-prayer" aria-label="Close prayer">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              If you would like to accept Jesus Christ as your Lord and Savior, you can pray this prayer sincerely from your heart. There is no magic in the words — what matters is the sincerity of your heart:
            </p>
            <Card className="mb-4">
              <CardContent className="p-5">
                <p className="italic text-sm leading-relaxed">
                  "Dear Lord Jesus, I know that I am a sinner and I ask for Your forgiveness. I believe You died for my sins and rose from the dead. I turn from my sins and invite You to come into my heart and life. I want to trust and follow You as my Lord and Savior. In Jesus' name, Amen."
                </p>
              </CardContent>
            </Card>
            <div className="flex gap-2 flex-wrap">
              <Button 
                onClick={async () => {
                  trackPrayerStarted();
                  const headers: Record<string, string> = { "Content-Type": "application/json" };
                  if (profile) headers["X-Profile-Id"] = profile.id;
                  await fetch("/api/salvation/prayed", { method: "POST", headers, body: JSON.stringify({}) });
                  const savedRes = await fetch("/api/salvation/saved", { method: "POST", headers, body: JSON.stringify({}) });
                  if (!savedRes.ok) {
                    console.error("Failed to mark saved:", savedRes.status);
                  }
                  trackSalvation();
                  recordSaved();
                  queryClient.invalidateQueries({ queryKey: ["/api/salvation/counter"] });
                  queryClient.invalidateQueries({ queryKey: ["/api/global/counters"] });
                  refetchSalvationStatus();
                  setShowSalvationPrayer(false);
                  toast({
                    title: "Welcome to the Family of God!",
                    description: "Heaven rejoices with you! Your name is written in the Lamb's Book of Life.",
                  });
                }}
                data-testid="button-prayed"
              >
                <Heart className="h-4 w-4 mr-2" />
                I Prayed This Prayer
              </Button>
              <Button 
                variant="outline" 
                onClick={async () => {
                  const askHeaders: Record<string, string> = { "Content-Type": "application/json" };
                  if (profile) askHeaders["X-Profile-Id"] = profile.id;
                  await fetch("/api/salvation/asked", { method: "POST", headers: askHeaders, body: JSON.stringify({}) });
                  refetchSalvationStatus();
                  setShowSalvationPrayer(false);
                }}
                data-testid="button-not-ready"
              >
                Not Ready Yet
              </Button>
            </div>
            <div className="mt-4 flex flex-col gap-1">
              <p className="text-xs text-muted-foreground italic">
                "For God so loved the world that he gave his one and only Son, that whoever believes in him shall not perish but have eternal life." — John 3:16
              </p>
              <p className="text-xs text-muted-foreground italic">
                "If you declare with your mouth, 'Jesus is Lord,' and believe in your heart that God raised him from the dead, you will be saved." — Romans 10:9
              </p>
            </div>
          </div>
        </div>
      )}

      {!isAuthLoading && !isAuthenticated && !guestBarDismissed && (
        <div
          className="mx-4 mt-2 flex items-center gap-3 rounded-lg border border-amber-500/20 bg-amber-500/[0.04] px-3 py-2 text-xs text-muted-foreground relative z-10"
          data-testid="banner-guest"
        >
          <span className="flex-1">
            {entitlement?.limits.retainedThreads != null && (allThreads?.length ?? 0) >= entitlement.limits.retainedThreads
              ? `Guests keep your last ${entitlement.limits.retainedThreads} conversations. Sign in to save all of them.`
              : "You're chatting as a guest — sign in to keep this conversation across devices."}
          </span>
          <Button size="sm" variant="outline" className="h-7 shrink-0" onClick={() => navigate("/login")} data-testid="button-guest-signin">
            Sign in
          </Button>
          <button
            type="button"
            aria-label="Dismiss"
            className="shrink-0 text-muted-foreground/60 hover:text-foreground"
            onClick={() => {
              sessionStorage.setItem("memnora-guest-bar-dismissed", "1");
              setGuestBarDismissed(true);
            }}
            data-testid="button-guest-dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <div className="px-4 pt-1 pb-0.5 relative z-10">
        <CovenantCoherenceBadge />
        {advancedResonance ? (
          <>
            <AdvancedResonanceBadges />
            <EngineModeBadge />
            {profile && <ResonanceChainPanel userId={`profile-${profile.id}`} />}
          </>
        ) : (
          <ResonanceInsightsTeaser onUnlock={goToUpgrade} />
        )}
      </div>

      {manifoldVisible && (
        <div className="px-4 pt-1 pb-1 relative z-10">
          <ErrorBoundary fallback={
            <div className="rounded-xl border border-amber-500/20 bg-[#0a0a1a] p-4 text-amber-400/60 text-sm" data-testid="manifold-error-fallback">
              Harmonic manifold encountered a rendering error. Your GPU may not support this visualization.
            </div>
          }>
          <HarmonicManifold
            points={messages
              .filter((m): m is Message & { harmonicAnalysis: HarmonicAnalysis } => !!m.harmonicAnalysis)
              .map((m, i): ManifoldPoint => ({
                messageId: m.id,
                index: i,
                analysis: m.harmonicAnalysis,
              }))}
            onPointClick={(messageId) => {
              const el = document.querySelector(`[data-testid="message-${messageId}"]`);
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }}
            height={240}
          />
          </ErrorBoundary>
        </div>
      )}

      <ScrollArea className="flex-1 relative z-0 min-h-0" ref={scrollRef}>
        <div className="max-w-3xl mx-auto px-4 py-6">
          {messages.length === 0 && (
            <div className="text-center py-16" data-testid="status-empty">
              <div className="flex items-center justify-center h-16 w-16 rounded-xl bg-primary text-primary-foreground font-bold text-2xl mx-auto mb-4" data-testid="logo-empty">
                T
              </div>
              <h2 className="text-xl font-semibold mb-2" data-testid="text-welcome-title">Welcome to {BRAND.platform}</h2>
              <p className="text-muted-foreground max-w-md mx-auto mb-2" data-testid="text-welcome-description">
                {BRAND.aiName} is a guide rooted in God's Word — here to walk with you through all of life. Schoolwork, career, everyday thinking, faith, and everything in between. God belongs in all of it.
              </p>
              <p className="text-xs text-muted-foreground/70 italic max-w-sm mx-auto" data-testid="text-welcome-verse">
                "In the beginning was the Word, and the Word was with God, and the Word was God." — John 1:1
              </p>
            </div>
          )}
          
          <div className="space-y-6">
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  "flex gap-4",
                  message.role === "user" ? "justify-end" : "justify-start"
                )}
                data-testid={`message-${message.id}`}
              >
                {message.role === "memnora" && (
                  <div className="flex-shrink-0 mt-1">
                    {message.resonance && message.resonance.nodes.length > 0 ? (
                      <div data-testid={`orb-resonance-${message.id}`}>
                        <ResonanceOrb frame={message.resonance} size={48} />
                      </div>
                    ) : (
                      <div className="memnora-avatar-glow">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="memnora-orb text-primary-foreground text-xs font-bold" data-testid={`avatar-memnora-${message.id}`}>
                            M
                          </AvatarFallback>
                        </Avatar>
                      </div>
                    )}
                  </div>
                )}
                <div
                  className={cn(
                    "max-w-[85%] sm:max-w-[75%] relative overflow-hidden",
                    message.role === "user"
                      ? "rounded-2xl rounded-br-md bg-primary text-primary-foreground"
                      : "rounded-2xl rounded-bl-md dark:bg-card/60 dark:backdrop-blur-sm message-resonance-wave",
                    message.role === "memnora" && message.breathPhase === "+1" && "coherence-positive",
                    message.role === "memnora" && message.breathPhase === "-1" && "coherence-negative",
                    message.role === "memnora" && message.breathPhase === "0" && "coherence-neutral"
                  )}
                >
                  {message.role === "memnora" && message.resonance && message.resonance.nodes.length > 0 && (
                    <div className="absolute inset-0 z-0 opacity-[0.25]" style={{ pointerEvents: "none" }} aria-hidden="true" data-testid={`backdrop-resonance-${message.id}`}>
                      <ResonanceMini2D frame={message.resonance} />
                    </div>
                  )}
                  <div className="relative z-[1] px-4 py-3">
                    {message.role === "memnora" && (
                      <div className="flex items-center justify-between mb-1 gap-2">
                        {message.breathPhase && (
                          <div className={cn("text-xs flex items-center gap-1", getBreathColor(message.breathPhase))} data-testid={`text-breath-${message.id}`}>
                            <Sparkles className="h-3 w-3" />
                            {message.breathPhase === "+1" ? "Resonance: Ascending" : message.breathPhase === "-1" ? "Resonance: Descending" : "Resonance: Centered"}
                          </div>
                        )}
                        {!message.breathPhase && <div />}
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {message.harmonicSensory && (
                            <Badge 
                              variant="outline" 
                              className="text-[10px] px-1.5 py-0 h-5 font-mono gap-1 border-transparent flex-shrink-0"
                              style={{ 
                                borderColor: `hsla(${message.harmonicSensory.dominantHue}, 70%, 50%, 0.4)`,
                                backgroundColor: `hsla(${message.harmonicSensory.dominantHue}, 70%, 50%, 0.08)`,
                                color: `hsl(${message.harmonicSensory.dominantHue}, 70%, 65%)`
                              }}
                              data-testid={`chip-harmonic-${message.id}`}
                              title={message.harmonicSensory.accessibilityLabel}
                              aria-label={message.harmonicSensory.accessibilityLabel}
                            >
                              <span 
                                className="h-2 w-2 rounded-full flex-shrink-0" 
                                style={{ backgroundColor: `hsl(${message.harmonicSensory.dominantHue}, 70%, 50%)` }}
                                aria-hidden="true"
                              />
                              {message.harmonicSensory.simplifiedTone}
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 font-mono gap-1 border-amber-500/30 bg-amber-500/5 flex-shrink-0" data-testid={`chip-coherence-${message.id}`}>
                            <span className={cn(
                              "h-1.5 w-1.5 rounded-full",
                              (message.resonance?.coherence ?? 0) > 0.6 ? "bg-amber-400" : (message.resonance?.coherence ?? 0) > 0.3 ? "bg-blue-400" : "bg-muted-foreground"
                            )} />
                            Coherence {Math.round((message.resonance?.coherence ?? 0) * 100)}%
                          </Badge>
                        </div>
                      </div>
                    )}
                    {message.attachments && message.attachments.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2" data-testid={`attachments-${message.id}`}>
                        {message.attachments.map((att) => (
                          <div key={att.id} className="rounded-md overflow-hidden border">
                            {att.type.startsWith("image/") && att.url ? (
                              <img 
                                src={att.url} 
                                alt={att.name}
                                className="max-w-[200px] max-h-[200px] object-cover"
                                data-testid={`attachment-image-${att.id}`}
                              />
                            ) : (
                              <div className="flex items-center gap-2 px-3 py-2 bg-muted" data-testid={`attachment-file-${att.id}`}>
                                <File className="h-4 w-4" />
                                <span className="text-xs truncate max-w-[150px]">{att.name}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className={cn(
                      "text-sm whitespace-pre-wrap leading-relaxed",
                      message.role === "memnora" && "text-foreground"
                    )} data-testid={`text-content-${message.id}`}>
                      {message.content}
                    </div>
                    {message.role === "memnora" && message.emotionMetadata && (() => {
                      const harmonicColor = resonanceAudioEnabled && message.harmonicAnalysis
                        ? deriveColorFromHarmonics(message.harmonicAnalysis, message.emotionMetadata!.colorHex)
                        : message.emotionMetadata!.colorHex;
                      return (
                      <div className="mt-2 pt-2 border-t border-border/30 flex flex-wrap items-center gap-2" data-testid={`emotion-bar-${message.id}`}>
                        <span
                          className="inline-flex items-center gap-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full"
                          style={{
                            backgroundColor: harmonicColor + (harmonicColor.startsWith("hsl") ? "" : "20"),
                            color: harmonicColor,
                            borderWidth: "1px",
                            borderColor: harmonicColor + (harmonicColor.startsWith("hsl") ? "" : "40"),
                            ...(harmonicColor.startsWith("hsl") ? { opacity: 0.9 } : {}),
                          }}
                          data-testid={`emotion-label-${message.id}`}
                        >
                          <span
                            className="h-1.5 w-1.5 rounded-full"
                            style={{ backgroundColor: harmonicColor }}
                          />
                          {message.emotionMetadata!.emotionLabel}
                          {message.emotionMetadata!.musical && (
                            <span className="ml-1 opacity-60 text-[8px]">
                              {message.emotionMetadata!.musical.mode}
                            </span>
                          )}
                        </span>
                        {message.harmonicAnalysis && (
                          <span
                            className="text-[9px] font-mono opacity-50 hidden sm:inline"
                            data-testid={`harmonic-sig-${message.id}`}
                            title={`${message.harmonicAnalysis.baseFrequency.toFixed(1)}Hz | centroid ${message.harmonicAnalysis.spectralCentroid.toFixed(0)}Hz | density ${message.harmonicAnalysis.harmonicDensity.toFixed(2)}`}
                          >
                            {message.harmonicAnalysis.key}{message.harmonicAnalysis.scaleDegree} · {Math.round(message.harmonicAnalysis.baseFrequency)}Hz
                          </span>
                        )}
                        <span className="text-[10px] text-muted-foreground italic" data-testid={`scripture-ref-${message.id}`}>
                          {message.emotionMetadata!.scriptureRef}
                        </span>
                        <span className="text-[10px] text-muted-foreground/60 hidden sm:inline" data-testid={`growth-direction-${message.id}`}>
                          → {message.emotionMetadata!.growthDirection}
                        </span>
                      </div>
                      );
                    })()}
                    <div className={cn(
                      "text-xs mt-2 flex items-center gap-2",
                      message.role === "user" ? "opacity-70" : "text-muted-foreground"
                    )} data-testid={`metadata-${message.id}`}>
                      <span data-testid={`text-time-${message.id}`}>{message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      {message.role === "memnora" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 opacity-40 hover:opacity-100"
                          onClick={() => copyToClipboard(message.id, message.content)}
                          data-testid={`button-copy-${message.id}`}
                          aria-label="Copy response"
                        >
                          {copiedMessageId === message.id ? (
                            <Check className="h-3 w-3 text-green-400" aria-hidden="true" />
                          ) : (
                            <Copy className="h-3 w-3" aria-hidden="true" />
                          )}
                        </Button>
                      )}
                      {message.role === "memnora" && speechSupported && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 opacity-40 hover:opacity-100"
                          onClick={() => speakMessage(message.id, message.content)}
                          data-testid={`button-speak-${message.id}`}
                          aria-label={speakingMessageId === message.id ? "Stop speaking" : "Speak message"}
                        >
                          {speakingMessageId === message.id ? (
                            <Square className="h-3 w-3" aria-hidden="true" />
                          ) : (
                            <Volume2 className="h-3 w-3" aria-hidden="true" />
                          )}
                        </Button>
                      )}
                      {message.role === "memnora" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 opacity-40 hover:opacity-100"
                          onClick={() => setReportingMessage({ id: message.id, content: message.content })}
                          data-testid={`button-report-${message.id}`}
                          aria-label="Report this response"
                        >
                          <Flag className="h-3 w-3" aria-hidden="true" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                {message.role === "user" && (
                  <Avatar className="h-8 w-8 flex-shrink-0 mt-1">
                    <AvatarFallback className="bg-muted text-xs" data-testid={`avatar-user-${message.id}`}>You</AvatarFallback>
                  </Avatar>
                )}
              </div>
            ))}

            {/* Chat-only mode: salvation/prayer engagement prompt hidden. */}
            {false && profile && !salvationStatus?.isSaved && !salvationStatus?.askedAboutSalvation && !showSalvationPrayer &&
              messages.filter(m => m.role === "user").length >= 3 && (
              <div className="flex gap-4 justify-start" data-testid="panel-engagement-prompt">
                <Avatar className="h-8 w-8 flex-shrink-0 mt-1">
                  <AvatarFallback className="memnora-orb text-primary-foreground text-xs font-bold">M</AvatarFallback>
                </Avatar>
                <Card className="max-w-[85%] sm:max-w-[75%]">
                  <CardContent className="p-4 space-y-3">
                    <p className="text-sm leading-relaxed">
                      I'm grateful for our conversation. If you ever want to explore what it means to know God personally, I'm here for you.
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowSalvationPrayer(true)}
                        className="gap-1"
                        data-testid="button-engagement-prayer"
                      >
                        <BookOpen className="h-3 w-3" />
                        Prayer of Salvation
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => markSavedMutation.mutate()}
                        disabled={markSavedMutation.isPending}
                        className="gap-1"
                        data-testid="button-engagement-saved"
                      >
                        <Heart className="h-3 w-3" />
                        I'm Already Saved
                      </Button>
                    </div>
                    <p className="text-[10px] text-muted-foreground italic">
                      "The Lord is near to all who call on him." — Psalm 145:18
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {(chatMutation.isPending || isStreaming) && (
              <div className="flex gap-4" role="status" aria-label="Memnora is responding" data-testid="status-responding" ref={activeMessageRef}>
                <div className="flex-shrink-0 mt-1">
                  {streamingResonanceRef.current && streamingResonanceRef.current.nodes.length > 0 ? (
                    <div data-testid="orb-resonance-streaming">
                      <ResonanceOrb frame={streamingResonanceRef.current} size={48} />
                    </div>
                  ) : (
                    <div className="memnora-avatar-glow">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="memnora-orb text-primary-foreground text-xs font-bold" data-testid="avatar-streaming">
                          M
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  )}
                </div>
                <div className="max-w-[85%] sm:max-w-[75%] relative overflow-hidden rounded-2xl rounded-bl-md dark:bg-card/60 dark:backdrop-blur-sm">
                  {streamingResonanceRef.current && streamingResonanceRef.current.nodes.length > 0 && (
                    <div className="absolute inset-0 z-0 opacity-[0.25]" style={{ pointerEvents: "none" }} aria-hidden="true" data-testid="backdrop-resonance-streaming">
                      <ResonanceMini2D frame={streamingResonanceRef.current} streaming={true} />
                    </div>
                  )}
                  <div className="relative z-[1] px-4 py-3">
                    {streamingContent ? (
                      <>
                        <div className="flex items-center justify-between mb-1 gap-2">
                          <div className="text-xs flex items-center gap-1 text-chart-1" data-testid="text-streaming-status">
                            <Sparkles className="h-3 w-3 animate-pulse" />
                            Responding...
                          </div>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 font-mono gap-1 border-amber-500/30 bg-amber-500/5 flex-shrink-0" data-testid="chip-coherence-streaming">
                            {streamingResonanceRef.current && streamingResonanceRef.current.coherence > 0 ? (
                              <>
                                <span className={cn(
                                  "h-1.5 w-1.5 rounded-full",
                                  streamingResonanceRef.current.coherence > 0.6 ? "bg-amber-400" : streamingResonanceRef.current.coherence > 0.3 ? "bg-blue-400" : "bg-muted-foreground"
                                )} />
                                Coherence {Math.round(streamingResonanceRef.current.coherence * 100)}%
                              </>
                            ) : (
                              <>
                                <span className="h-1.5 w-1.5 rounded-full bg-amber-400/50 animate-pulse" />
                                <span className="text-muted-foreground animate-pulse">Aligning...</span>
                              </>
                            )}
                          </Badge>
                        </div>
                        <div className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="text-streaming-content">{streamingContent}</div>
                      </>
                    ) : (
                      <div className="flex items-center gap-2 py-2" aria-hidden="true" data-testid="status-loading-dots">
                        <span className="h-2 w-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="h-2 w-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="h-2 w-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      <div className="border-t px-3 py-2 sm:px-4 sm:py-3 bg-background/80 dark:bg-background/40 backdrop-blur-sm relative z-10 safe-bottom">
        {attachments.length > 0 && (
          <div className="max-w-3xl mx-auto mb-2 flex flex-wrap gap-2">
            {attachments.map((attachment) => (
              <div
                key={attachment.id}
                className="flex items-center gap-2 bg-muted rounded-lg px-3 py-1.5"
                data-testid={`attachment-${attachment.id}`}
              >
                {attachment.type.startsWith("image/") ? (
                  <Image className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <File className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-sm truncate max-w-[120px]" data-testid={`attachment-name-${attachment.id}`}>
                  {attachment.name}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => removeAttachment(attachment.id)}
                  data-testid={`button-remove-attachment-${attachment.id}`}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
        {nearDailyLimit && (
          <div
            className="max-w-3xl mx-auto mb-1.5 flex items-center justify-between text-[11px] text-muted-foreground"
            data-testid="status-quota-meter"
          >
            <span>{entitlement?.usedToday} of {entitlement?.limits.dailyMessages} messages today</span>
            <button
              type="button"
              onClick={goToUpgrade}
              className="text-amber-400/80 underline underline-offset-2 hover:text-amber-300"
              data-testid="button-quota-upgrade"
            >
              Upgrade for more
            </button>
          </div>
        )}
        <form
          className="max-w-3xl mx-auto space-y-1.5"
          onSubmit={(e) => { e.preventDefault(); handleSend(); }}
          role="form"
          aria-label="Chat message form"
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            accept=".pdf,.doc,.docx,.epub,.txt,.md,.rtf,.csv,.png,.jpg,.jpeg,.gif,.webp,.heic"
            className="hidden"
            data-testid="input-file-upload"
          />
          <div className="flex gap-2 items-end">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-10 w-10 sm:h-9 sm:w-9 flex-shrink-0 touch-manipulation"
              onClick={() => fileInputRef.current?.click()}
              data-testid="button-attach-file"
              aria-label="Attach file"
            >
              <Paperclip className="h-5 w-5 sm:h-4 sm:w-4" />
            </Button>
            <div className="flex-1 relative cosmic-input-glow rounded-md">
              <Textarea
                placeholder="Message Memnora..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                onFocus={() => {}}
                className="resize-none dark:bg-card/80 dark:backdrop-blur-sm min-h-[40px]"
                data-testid="input-chat-message"
                aria-label="Message to Memnora"
                rows={1}
              />
            </div>
            {speechRecognitionSupported && (
              <Button
                type="button"
                variant={isRecording ? "destructive" : "ghost"}
                size="icon"
                className={cn(
                  "h-10 w-10 sm:h-9 sm:w-9 flex-shrink-0 touch-manipulation",
                  isRecording && "animate-pulse ring-2 ring-red-500/50"
                )}
                onClick={toggleRecording}
                disabled={chatMutation.isPending || isStreaming}
                data-testid="button-mic-toggle"
                aria-label={isRecording ? "Stop recording" : "Voice input"}
              >
                {isRecording ? (
                  <MicOff className="h-5 w-5 sm:h-4 sm:w-4" aria-hidden="true" />
                ) : (
                  <Mic className="h-5 w-5 sm:h-4 sm:w-4" aria-hidden="true" />
                )}
              </Button>
            )}
            <Button 
              type="submit"
              size="icon"
              className="h-10 w-10 sm:h-9 sm:w-9 flex-shrink-0 touch-manipulation"
              disabled={(!input.trim() && attachments.length === 0) || chatMutation.isPending || isStreaming}
              data-testid="button-send-message"
              aria-label="Send message"
            >
              <Send className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
          <div className="flex items-center justify-between px-1">
            <Select value={responseMode} onValueChange={setResponseMode}>
              <SelectTrigger className="w-28 h-7 text-[11px] border-0 bg-transparent touch-manipulation" data-testid="select-response-mode">
                <SelectValue placeholder="Mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="balanced">Balanced</SelectItem>
                <SelectItem value="reflective">Reflective</SelectItem>
                <SelectItem value="analytical">Analytical</SelectItem>
                <SelectItem value="scriptural">Scriptural</SelectItem>
                <SelectItem value="emotional">Emotional</SelectItem>
                <SelectItem value="decision">Decision</SelectItem>
              </SelectContent>
            </Select>
            {isRecording && (
              <span className="text-[11px] text-red-400 animate-pulse" data-testid="text-recording-status">Recording...</span>
            )}
          </div>
        </form>
        <p className="text-[11px] text-muted-foreground text-center mt-1.5 max-w-3xl mx-auto" data-testid="text-footer-info">
          Free discipleship — Memnora is here for you
        </p>
      </div>


      {showProfileModal && (
        <ProfileModal onClose={() => setShowProfileModal(false)} />
      )}

      <Dialog open={!!reportingMessage} onOpenChange={(open) => { if (!open) { setReportingMessage(null); setReportReason("offensive"); setReportDetails(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-report-content">
          <DialogHeader>
            <DialogTitle>Report AI Response</DialogTitle>
            <DialogDescription>
              Help us maintain content quality. Select a reason and optionally provide details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <RadioGroup value={reportReason} onValueChange={setReportReason} data-testid="radio-report-reason">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="offensive" id="r-offensive" />
                <Label htmlFor="r-offensive">Offensive or inappropriate</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="inaccurate" id="r-inaccurate" />
                <Label htmlFor="r-inaccurate">Biblically inaccurate</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="harmful" id="r-harmful" />
                <Label htmlFor="r-harmful">Potentially harmful advice</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="inappropriate" id="r-inappropriate" />
                <Label htmlFor="r-inappropriate">Not appropriate for audience</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="other" id="r-other" />
                <Label htmlFor="r-other">Other</Label>
              </div>
            </RadioGroup>
            <Textarea
              placeholder="Additional details (optional)"
              value={reportDetails}
              onChange={(e) => setReportDetails(e.target.value)}
              className="min-h-[80px]"
              maxLength={1000}
              data-testid="input-report-details"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportingMessage(null)} data-testid="button-report-cancel">Cancel</Button>
            <Button onClick={submitContentReport} disabled={reportSubmitting} data-testid="button-report-submit">
              {reportSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
